import React, { forwardRef, RefObject, SyntheticEvent, useState } from 'react';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { metaTrigger } from '@support/keyboardHandler';
import Pill from '@components/Pill';
import styles from './ContactSearch.module.scss';
import Contact from '@models/Contact';
import { useContactSearch } from '@hooks/use-contact-search';
import { useSelector } from 'react-redux';
import { selectContact } from '@actions/contacts';
import { IDRecord } from '@app/types';

interface ContactSearchProps {
  contact?: Contact;
  disabled?: boolean;
  onContact: (contact: Contact) => void;
  onLookup: (displayName: string) => void;
  onSubmit: (e?: SyntheticEvent) => void;
  contactNotEditable?: boolean;
}

const ContactPill = ({
  idRecord,
  onClick,
  editable,
  className,
}: {
  idRecord: IDRecord;
  onClick: () => void;
  editable?: boolean;
  className: string;
}) => {
  const contact = useSelector(selectContact(idRecord.id));
  return <Pill className={className} contact={contact} onClick={onClick} editable={editable} />;
};

function ContactSearch(props: ContactSearchProps, ref: RefObject<HTMLInputElement>) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [setContactSearchTerm, contactSearchState] = useContactSearch({
    searchAttributes: ['displayName', 'searchLookups'],
    defaultTerm: '',
  });
  const searchResults = contactSearchState.results.items.valueSeq().toArray();
  const handleRemove = () => props.onContact(null);

  const handleSelectContact = (contact) => {
    if (props.disabled) {
      return;
    }

    setContactSearchTerm('');
    setHighlightedIndex(-1);
    props.onLookup('');
    props.onContact(contact);
  };

  const moveHighlightRight = () => {
    const nextIndex = highlightedIndex + 1;
    setHighlightedIndex(nextIndex < searchResults.length ? nextIndex : 0);
  };

  const moveHighlightLeft = () => {
    const nextIndex = highlightedIndex - 1;
    setHighlightedIndex(nextIndex > -1 ? nextIndex : searchResults.length - 1);
  };

  const handleKeyDown = (e) => {
    if (metaTrigger(e) && e.key == 'Enter') {
      return props.onSubmit?.(e);
    }

    if (e.key == 'Enter' && highlightedIndex > -1) {
      handleSelectContact(searchResults[highlightedIndex]);
    }

    if (searchResults.length > 0) {
      if (['Tab', 'ArrowDown'].includes(e.key)) {
        moveHighlightRight();
      } else if (e.key == 'ArrowUp') {
        moveHighlightLeft();
      }
    }
  };

  const onInputChange = (e) => {
    props.onLookup(e.target.value);
    setContactSearchTerm(e.target.value);
  };

  if (props.contact) {
    return (
      <ContactPill
        idRecord={props.contact as IDRecord}
        className={styles.pill}
        editable={!props.contactNotEditable}
        onClick={!props.contactNotEditable ? handleRemove : undefined}
      />
    );
  }

  return (
    <>
      <input
        type="text"
        id="person"
        className={cn(styles.input, 'form-input-transparent')}
        ref={ref}
        value={contactSearchState.term}
        placeholder="Enter a Name, Email, LinkedIn, Twitter, or Phone..."
        onChange={onInputChange}
        onKeyDown={handleKeyDown}
        autoFocus
        autoComplete="off"
        disabled={props.disabled}
      />
      <CSSTransition
        in={searchResults.length > 0}
        classNames={{
          appear: styles.suggestionsEnter,
          appearActive: styles.suggestionsEnterActive,
          appearDone: styles.suggestionsEnterDone,
          enter: styles.suggestionsEnter,
          enterActive: styles.suggestionsEnterActive,
          enterDone: styles.suggestionsEnterDone,
          exitActive: styles.suggestionsExit,
        }}
        timeout={200}
        appear
      >
        <div className={styles.suggestionsContainer}>
          <div className={styles.suggestions}>
            {searchResults.map((suggestion, i) => (
              <ContactPill
                key={suggestion.id}
                idRecord={suggestion}
                className={cn(
                  styles.suggestion,
                  highlightedIndex == i && styles.suggestionHighlighted,
                )}
                onClick={() => handleSelectContact(suggestion)}
              />
            ))}
          </div>
        </div>
      </CSSTransition>
    </>
  );
}

export default forwardRef(ContactSearch);
